// src/lib/guided/grade.ts
//
// Phase 3.1 of docs/guided-shopping_plan.md: `gradeLead(config, answers)`
// grades a submitted set of answers per
// docs/sales-methodologies_research.md §5.2 item 5. The owner sees the
// grade and reasons (3.7); the visitor never does (3.6).
//
// Reads generically off `criterion.type`, the same design presets.ts
// already documents: a preset's only lever over grading is which criterion
// types (and, since this task, which `topBuckets`/`belowFloorBuckets`) it
// includes, not a per-preset grading table.
import type { FunnelConfig, FunnelCriterion } from "./config";
import {
  NOT_SURE_YET_OPTION,
  PREFER_TO_DISCUSS_OPTION,
  URGENCY_FAMILY_TYPES,
  effectiveOfferFit,
} from "./compile";
import { survivorsForBucket } from "./order";
import { resolveOutcome, POLITE_NO_OUTCOME_ID } from "./outcomes";

export type LeadGrade = "A" | "B" | "C";

export interface LeadGradeResult {
  grade: LeadGrade;
  nurture: boolean;
  reasons: string[];
}

/**
 * Plan 3.7: how the owner reads a grade, wherever it appears -- the dashboard
 * pill (CLAUDE.md §8), the CRM note (`buildContactSummary`), and the
 * notification email/in-app message. `emailColor` exists because an HTML
 * email cannot load Tailwind; `badgeClass`/`dotClass` follow the same
 * label/dot pattern as `HITL_STATUS_PRESENTATION` (src/lib/hitl-status.ts).
 */
export interface LeadGradePresentation {
  label: string;
  badgeClass: string;
  dotClass: string;
  emailColor: string;
}

/**
 * Labels and colors from docs/guided-shopping_research.md §7.2: "A, hot ...
 * B, warm ... C, not a fit". Green/blue/slate rather than red for C, because a
 * bad-fit lead is not a failure of the product -- it is the junk filter and
 * the fit rules doing their job.
 */
export const LEAD_GRADE_PRESENTATION: Record<LeadGrade, LeadGradePresentation> =
  {
    A: {
      label: "Hot lead",
      badgeClass: "bg-green-100 text-green-800",
      dotClass: "bg-green-500",
      emailColor: "#166534",
    },
    B: {
      label: "Warm lead",
      badgeClass: "bg-blue-100 text-blue-800",
      dotClass: "bg-blue-500",
      emailColor: "#1e40af",
    },
    C: {
      label: "Not a fit",
      badgeClass: "bg-slate-100 text-slate-700",
      dotClass: "bg-slate-400",
      emailColor: "#475569",
    },
  };

/**
 * The pill for a grade string that has not been validated against the union
 * (e.g. straight out of `Submission.metadata`, a Prisma `Json?` column that
 * can hold anything). Returns null rather than throwing, matching
 * `hitlStatusPill`'s "the dashboard renders whatever the column holds"
 * discipline.
 */
export function leadGradePresentation(
  grade: string,
): LeadGradePresentation | null {
  return grade === "A" || grade === "B" || grade === "C"
    ? LEAD_GRADE_PRESENTATION[grade]
    : null;
}

/**
 * A-grade leads bypass the owner's "notify me" toggle and get a different
 * subject line (plan 3.7, sourced from docs/guided-shopping_research.md's
 * "Lead grade with instant alert for A leads"). Everything else -- B, C --
 * follows the form's existing notification setting.
 */
export function isHotLead(grade: LeadGrade): boolean {
  return grade === "A";
}

/** The notification subject prefix for a graded submission (plan 3.7). */
export function leadGradeSubjectPrefix(grade: LeadGrade): string {
  return isHotLead(grade) ? "Hot lead" : "New lead";
}

/**
 * Reads back what `gradeSubmission` (src/lib/database/submissions.ts, plan
 * 3.4) stored on `Submission.metadata.leadGrade`. Total over any JSON shape:
 * a missing key, a non-object, or a `grade` outside "A"|"B"|"C" all read as
 * "no grade" rather than throwing, because `metadata` is a Prisma `Json?`
 * column and most submissions (every non-guided-shopping form) store none of
 * these keys at all.
 */
export function readStoredLeadGrade(
  metadata: unknown,
): { grade: LeadGrade; reasons: string[] } | null {
  if (!metadata || typeof metadata !== "object") return null;
  const leadGrade = (metadata as Record<string, unknown>).leadGrade;
  if (!leadGrade || typeof leadGrade !== "object") return null;
  const grade = (leadGrade as Record<string, unknown>).grade;
  if (grade !== "A" && grade !== "B" && grade !== "C") return null;
  const reasonsRaw = (leadGrade as Record<string, unknown>).reasons;
  const reasons = Array.isArray(reasonsRaw)
    ? reasonsRaw.filter(
        (reason): reason is string => typeof reason === "string",
      )
    : [];
  return { grade, reasons };
}

/**
 * Plan 3.1 acceptance: owner-facing help text for the dashboard (Phase 4
 * shows it next to the grade; no dashboard surface exists yet to render it
 * from, so it is a plain exported string until one does). Sales research
 * §4.4: asking about timing nudges follow-through, which means an early
 * check of the grade's accuracy will look better than the grade actually is.
 */
export const GRADE_ACCURACY_DISCLAIMER =
  "An early accuracy check of this grade will look better than it is, because asking about timing nudges people to follow through.";

function isHedgeValue(value: string): boolean {
  return (
    value === String(NOT_SURE_YET_OPTION.value) ||
    value === String(PREFER_TO_DISCUSS_OPTION.value)
  );
}

/** The visitor's answer to `criterionId`, or null when unanswered/blank. */
function answeredValue(
  answers: Record<string, unknown>,
  criterionId: string,
): string | null {
  const value = answers[criterionId];
  return typeof value === "string" && value !== "" ? value : null;
}

function bucketLabel(criterion: FunnelCriterion, value: string): string {
  return (
    criterion.buckets.find((bucket) => bucket.id === value)?.label ?? value
  );
}

/**
 * A hard gate or authority gate that, on its own answer, rules out every
 * offer -- independent of every other criterion (research §5.2: "a hard
 * gate failed gives C"). Checked one criterion at a time against the full
 * offer list, never the running survivor set a full walk (like
 * `resolveOutcome`'s) would narrow, because a later non-gate criterion
 * narrowing to zero is "no offer fits" (a different reason below), not a
 * failed gate.
 */
function failedGateCriterion(
  config: FunnelConfig,
  answers: Record<string, unknown>,
): { criterion: FunnelCriterion; value: string } | null {
  const offerFit = effectiveOfferFit(config);
  const allOfferIds = config.offers.map((offer) => offer.id);
  for (const criterion of config.criteria) {
    if (criterion.type !== "gate" && criterion.type !== "authorityGate") {
      continue;
    }
    const value = answeredValue(answers, criterion.id);
    if (value === null || isHedgeValue(value)) continue;
    const survivors = survivorsForBucket(
      criterion.id,
      value,
      allOfferIds,
      offerFit,
    );
    if (survivors.length === 0) return { criterion, value };
  }
  return null;
}

/**
 * The first urgency-family criterion whose answer is one of that
 * criterion's own `topBuckets` (research §5.2: "fit plus an urgent trigger
 * or critical event, or 'ready to book', gives A"). Which bucket counts as
 * top varies by type -- `timing`/`trigger` read most-urgent-first in every
 * shipped preset, `stage` reads least-committed-first -- so there is no
 * positional default: a criterion opts a bucket in via `topBuckets` (plan
 * 3.1's "the owner can override which buckets count as top").
 */
function topUrgencyCriterion(
  config: FunnelConfig,
  answers: Record<string, unknown>,
): { criterion: FunnelCriterion; value: string } | null {
  for (const criterion of config.criteria) {
    if (!(URGENCY_FAMILY_TYPES as readonly string[]).includes(criterion.type)) {
      continue;
    }
    const value = answeredValue(answers, criterion.id);
    if (value === null || isHedgeValue(value)) continue;
    // `?? []`, not a direct read: a hand-built `FunnelConfig` (this module's
    // own tests, and `compile.ts`'s WEB_AGENCY_CONFIG fixture) can predate
    // this task and omit `topBuckets` entirely, despite the zod schema
    // defaulting it (2.9's own note on the identical `concerns` trap).
    const topBuckets = criterion.topBuckets ?? [];
    if (topBuckets.includes(value)) return { criterion, value };
  }
  return null;
}

/**
 * Money bands are always authored as ascending distance from the owner's
 * floor (research §3a.2: "Under [floor] / [floor]-2x / Over 2x / Not sure
 * yet"), so the first bucket is "under floor" by convention unless the
 * criterion overrides it with an explicit `belowFloorBuckets`.
 */
function isBelowFloor(criterion: FunnelCriterion, value: string): boolean {
  const belowFloorBuckets = criterion.belowFloorBuckets ?? [];
  if (belowFloorBuckets.length > 0) {
    return belowFloorBuckets.includes(value);
  }
  return criterion.buckets[0]?.id === value;
}

function moneyDemotionCriterion(
  config: FunnelConfig,
  answers: Record<string, unknown>,
): { criterion: FunnelCriterion; value: string } | null {
  for (const criterion of config.criteria) {
    if (criterion.type !== "money") continue;
    const value = answeredValue(answers, criterion.id);
    if (value === null || isHedgeValue(value)) continue;
    if (isBelowFloor(criterion, value)) return { criterion, value };
  }
  return null;
}

function demote(grade: LeadGrade): LeadGrade {
  if (grade === "A") return "B";
  return "C";
}

/**
 * Research §5.2: "'just researching' plus priorAttempts or habit 'putting
 * up with it' goes to nurture" -- a guide and follow-up, not a call.
 * Independent of the grade itself: a visitor can be a clean fit for an
 * offer and still be too early in deciding to call today.
 */
function computeNurture(
  config: FunnelConfig,
  answers: Record<string, unknown>,
): boolean {
  const stageCriterion = config.criteria.find(
    (criterion) => criterion.type === "stage",
  );
  if (!stageCriterion) return false;
  const stageValue = answeredValue(answers, stageCriterion.id);
  if (stageValue === null || isHedgeValue(stageValue)) return false;
  const researchingBucket = stageCriterion.buckets.find((bucket) =>
    /research/i.test(bucket.label),
  );
  if (!researchingBucket || researchingBucket.id !== stageValue) return false;

  return config.criteria.some((criterion) => {
    if (criterion.type !== "priorAttempts") return false;
    const value = answeredValue(answers, criterion.id);
    return value !== null && !isHedgeValue(value);
  });
}

/**
 * Grades one submission's answers: `{ grade, nurture, reasons }` per
 * docs/sales-methodologies_research.md §5.2 item 5. `answers` is keyed by
 * criterion id, the same shape `resolveOutcome` (outcomes.ts) reads.
 */
export function gradeLead(
  config: FunnelConfig,
  answers: Record<string, unknown>,
): LeadGradeResult {
  const failedGate = failedGateCriterion(config, answers);
  if (failedGate) {
    return {
      grade: "C",
      nurture: false,
      reasons: [
        `Answered "${bucketLabel(failedGate.criterion, failedGate.value)}" to "${failedGate.criterion.question}", which rules out every option`,
      ],
    };
  }

  const outcome = resolveOutcome(config, answers);
  if (outcome.id === POLITE_NO_OUTCOME_ID) {
    return {
      grade: "C",
      nurture: false,
      reasons: ["None of the offers matched their answers"],
    };
  }

  const reasons: string[] = [`Matches "${outcome.title}"`];
  let grade: LeadGrade;
  const topUrgency = topUrgencyCriterion(config, answers);
  if (topUrgency) {
    grade = "A";
    reasons.push(
      `Answered "${bucketLabel(topUrgency.criterion, topUrgency.value)}" to "${topUrgency.criterion.question}"`,
    );
  } else {
    grade = "B";
  }

  const moneyDemotion = moneyDemotionCriterion(config, answers);
  if (moneyDemotion) {
    grade = demote(grade);
    reasons.push(
      `"${bucketLabel(moneyDemotion.criterion, moneyDemotion.value)}" on "${moneyDemotion.criterion.question}" is below the usual starting budget`,
    );
  }

  return { grade, nurture: computeNurture(config, answers), reasons };
}
